import type { KeyboardEvent } from 'react'
import { CornerUpRight, GripVertical, Pencil, X } from 'lucide-react'

import { MessageAttachment } from '@/components/ai/message'
import { SortableDragHandle, SortableItem } from '@/components/custom/sortable'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { toFileUIPart } from '@/lib/attachments'
import { cn } from '@/lib/utils'
import type { QueuedMessage } from '@/stores/messageQueue'

import type { Presence } from './useListPresence'

interface QueuedMessageRowProps {
  item: QueuedMessage
  presence: Presence | undefined
  /** The leaving row finished collapsing and can unmount. */
  onExited: () => void
  /** "2 of 3": read out with every row button so the target is unambiguous. */
  position: string
  isEditing: boolean
  /** Another row is being edited; starting a second edit would drop it. */
  editLocked: boolean
  isRunActive: boolean
  onMoveKey: (e: KeyboardEvent<HTMLButtonElement>) => void
  onEdit: () => void
  onCancelEdit: () => void
  onPromote: () => void
  onRemove: () => void
}

function IconTooltip({
  tip,
  children,
}: {
  tip: string
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  )
}

export function QueuedMessageRow({
  item,
  presence,
  onExited,
  position,
  isEditing,
  editLocked,
  isRunActive,
  onMoveKey,
  onEdit,
  onCancelEdit,
  onPromote,
  onRemove,
}: QueuedMessageRowProps) {
  const leaving = presence === 'leaving'
  return (
    <SortableItem
      value={item.id}
      asChild
      className="data-[state=dragging]:opacity-100">
      {/* The li is a grid track so the row height can ease open and shut. */}
      <li
        aria-current={isEditing ? 'true' : undefined}
        aria-hidden={leaving || undefined}
        inert={leaving}
        data-entering={presence === 'entering' || undefined}
        data-leaving={leaving || undefined}
        onTransitionEnd={(e) => {
          if (leaving && e.target === e.currentTarget) onExited()
        }}
        className={cn(
          'group/row grid grid-rows-[1fr] transition-[grid-template-rows,opacity,translate] duration-fast ease-smooth-out data-[state=dragging]:relative data-[state=dragging]:z-10',
          'data-entering:starting:grid-rows-[0fr] data-entering:starting:translate-y-2 data-entering:starting:opacity-0',
          'data-leaving:grid-rows-[0fr] data-leaving:overflow-hidden data-leaving:opacity-0 data-leaving:duration-quick'
        )}>
        <div
          className={cn(
            'flex min-h-0 items-center gap-1 rounded-md px-0.5 py-0.5 transition-[padding,scale,box-shadow,background-color,color] duration-fast ease-smooth-out',
            'group-data-entering/row:starting:py-0 group-data-leaving/row:py-0 group-data-leaving/row:duration-quick',
            'group-data-[state=dragging]/row:scale-[1.02] group-data-[state=dragging]/row:bg-background group-data-[state=dragging]/row:shadow-md',
            isEditing && 'bg-accent text-accent-foreground'
          )}>
          <SortableDragHandle
            variant="ghost"
            size="icon-xs"
            aria-label={`Reorder queued message ${position} (drag, or press the arrow keys)`}
            className="text-muted-foreground"
            onKeyDown={onMoveKey}>
            <GripVertical />
          </SortableDragHandle>
          {item.attachments.map((a) => (
            <MessageAttachment
              key={a.id}
              data={toFileUIPart(a)}
              className="size-4 shrink-0 rounded-sm [&_svg]:size-3"
            />
          ))}
          {/* Visually truncated only: assistive tech reads the whole text, and
            the pencil loads it into the composer for everyone else. */}
          <span
            title={item.text}
            className="min-w-0 flex-1 truncate text-foreground/80">
            {isEditing && (
              <span className="sr-only">Editing queued message: </span>
            )}
            {item.text}
          </span>
          {isEditing ? (
            <Button
              size="xs"
              variant="ghost"
              className="transition-[opacity,translate,filter,color,background-color,scale] starting:translate-x-1 starting:opacity-0 starting:blur-[2px]"
              aria-label={`Cancel editing queued message ${position}`}
              onClick={onCancelEdit}>
              Cancel
            </Button>
          ) : (
            <>
              <IconTooltip tip="Edit in the composer">
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`Edit queued message ${position}`}
                  className="text-muted-foreground"
                  disabled={editLocked}
                  onClick={onEdit}>
                  <Pencil />
                </Button>
              </IconTooltip>
              <IconTooltip
                tip={
                  isRunActive
                    ? 'Send to David now, while it is still working'
                    : 'David is not working right now'
                }>
                {/* A disabled button gets no pointer events, so the span carries the tooltip. */}
                <span className="flex shrink-0">
                  <Button
                    size="xs"
                    variant="ghost"
                    aria-label={`Send queued message ${position} now`}
                    className="text-muted-foreground"
                    disabled={!isRunActive}
                    onClick={onPromote}>
                    <CornerUpRight />
                    Send now
                  </Button>
                </span>
              </IconTooltip>
              <IconTooltip tip="Remove from queue">
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`Remove queued message ${position}`}
                  className="text-muted-foreground"
                  onClick={onRemove}>
                  <X />
                </Button>
              </IconTooltip>
            </>
          )}
        </div>
      </li>
    </SortableItem>
  )
}
